import React, { useEffect, useMemo, useState } from 'react';
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Badge } from 'primereact/badge';

import { useTranslation } from '@/config/i18n';
import { MENU_COACH_TRIAL_ITEMS, MENU_ITEMS } from '@/shared/constants/menu';
import { getVisibleMenu } from '@/lib/utils';
import usePathName from '@/hooks/usePathName';
import Logo from '@/components/common/logo';
import PATH from '@/routes/path';
import { useSelectorUserInfo } from '@/components/auth/AuthContext';
import { IconArrowUp } from '@/components/icons';
import { useChat } from '@/components/chat/ChatContext';
import { SidebarFooter } from './SideBarFooter';
import { ROLE, STAGE_GRADUATION_STATUS } from '@/shared/constants/user';

type Props = {
  isOpen: boolean;
  toggleSidebar: () => void;
};

const chatPaths = [
  `${PATH.chat}/${PATH.chat_contact}`,
  `${PATH.chat}/${PATH.chat_conversation}`,
];

const LeftSideBar: React.FC<Props> = ({ isOpen, toggleSidebar }) => {
  const [t] = useTranslation('');
  const pathname = usePathName();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { currentUser } = useSelectorUserInfo();
  const { hasNewMessage } = useChat();
  const { current_roles: userRole, stage_graduation_status } =
    currentUser || {};
  const isCoachTrial =
    userRole[0] === ROLE.TEACHER &&
    stage_graduation_status === STAGE_GRADUATION_STATUS.TRIAL;

  const activeSubKey = useMemo(
    () =>
      MENU_ITEMS.find((menu) =>
        menu.children?.some((child) => pathname.startsWith(child.link)),
      )?.key,
    [pathname],
  );

  useEffect(() => {
    if (activeSubKey && !openKeys.includes(activeSubKey)) {
      setOpenKeys((prev) => [...prev, activeSubKey]);
    }
  }, [activeSubKey]);

  const handleOpenSubMenu = (key?: string) => {
    if (!key) return;

    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  return (
    <div className="flex flex-col fixed z-40 bg-primary-900">
      <Sidebar
        collapsed={!isOpen}
        backgroundColor="none"
        className="bg-shipGray h-[calc(100vh-171px)] transition-all duration-300"
        collapsedWidth="76px"
        width="280px"
      >
        <div className="logo flex-full-center my-[20px]">
          <Link to={PATH.home}>
            <Logo />
          </Link>
        </div>
        <Menu>
          {getVisibleMenu(
            isCoachTrial ? MENU_COACH_TRIAL_ITEMS : MENU_ITEMS,
            currentUser?.current_roles,
          ).map((item, index) => {
            const Icon = item.icon;
            const { children } = item;
            const isSubMenuOpen = item.key
              ? openKeys.includes(item.key)
              : false;
            const isParentActive = children?.some((child) =>
              pathname.startsWith(child.link),
            );

            return (
              <div
                key={index}
                className={classNames('sidebar-menu-item', {
                  'sidebar-menu-item-active': isParentActive,
                  'hide-menu': !isOpen,
                })}
              >
                {children?.length ? (
                  <SubMenu
                    icon={Icon ? <Icon /> : null}
                    label={t(item.title)}
                    open={isSubMenuOpen}
                    onClick={() => handleOpenSubMenu(item.key)}
                    className={classNames('text-primary-100', {
                      'active-parent': isParentActive,
                    })}
                    rootStyles={{
                      ['.ps-submenu-content']: {
                        backgroundColor: 'transparent',
                      },
                    }}
                    suffix={<IconArrowUp />}
                  >
                    {getVisibleMenu(children, currentUser?.current_roles).map(
                      (child, idx) => (
                        <MenuItem
                          key={idx}
                          className={classNames(
                            'text-primary-100 mt-[4px] h-[40px] pl-[25px]',
                            {
                              '!bg-primary-800 rounded-[16px] active-item':
                                pathname.startsWith(child.link),
                            },
                          )}
                          icon={child.icon || null}
                          component={<Link to={{ pathname: child.link }} />}
                        >
                          {t(child.title)}
                        </MenuItem>
                      ),
                    )}
                  </SubMenu>
                ) : (
                  <MenuItem
                    className="text-primary-100"
                    icon={Icon ? <Icon /> : null}
                    component={
                      !item.is_link_external ? (
                        <Link to={{ pathname: item.link }} />
                      ) : (
                        <a href={item.link} target="_blank"></a>
                      )
                    }
                    active={pathname.startsWith(item.link)}
                    onClick={() => handleOpenSubMenu()}
                    suffix={
                      chatPaths.includes(item.link) &&
                      hasNewMessage && (
                        <Badge
                          className="h-[14px] w-[14px]"
                          severity="danger"
                        />
                      )
                    }
                  >
                    {t(item.title)}
                  </MenuItem>
                )}
              </div>
            );
          })}
        </Menu>
      </Sidebar>
      <SidebarFooter collapsed={isOpen} toggleSidebar={toggleSidebar} />
    </div>
  );
};

export default LeftSideBar;
